"use client";
import {NeoError} from "@/components/shared";
import {useAllVehicleContext} from "@/context/live/allvehicle.context";
import {useLiveContext} from "@/context/live/live.context";
import {useNeoContext} from "@/context/neo/neo.context";
import {Spinner} from "@nextui-org/react";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  Polyline,
  useJsApiLoader
} from "@react-google-maps/api";

import {calcTime} from "@/libs/helper";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Info} from "./info";

const mapKey: string = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";
type HoverInfo = {
  latLan: google.maps.LatLng | null;
  info: SpeedProps;
};
export const TripMapView: React.FC<any> = ({page, tripdata}) => {
  const darkModeStyle = [
    {elementType: "geometry", stylers: [{color: "#212121"}]},
    {elementType: "labels.icon", stylers: [{visibility: "off"}]},
    {elementType: "labels.text.fill", stylers: [{color: "#757575"}]},
    {elementType: "labels.text.stroke", stylers: [{color: "#212121"}]},
    {
      featureType: "administrative",
      elementType: "geometry",
      stylers: [{color: "#757575"}]
    },
    {
      featureType: "administrative.country",
      elementType: "labels.text.fill",
      stylers: [{color: "#9e9e9e"}]
    },
    {featureType: "administrative.land_parcel", stylers: [{visibility: "off"}]},
    {
      featureType: "administrative.locality",
      elementType: "labels.text.fill",
      stylers: [{color: "#bdbdbd"}]
    },
    {
      featureType: "poi",
      elementType: "labels.text.fill",
      stylers: [{color: "#757575"}]
    },
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: [{color: "#181818"}]
    },
    {
      featureType: "poi.park",
      elementType: "labels.text.fill",
      stylers: [{color: "#616161"}]
    },
    {
      featureType: "poi.park",
      elementType: "labels.text.stroke",
      stylers: [{color: "#1b1b1b"}]
    },
    {
      featureType: "road",
      elementType: "geometry.fill",
      stylers: [{color: "#2c2c2c"}]
    },
    {
      featureType: "road",
      elementType: "labels.text.fill",
      stylers: [{color: "#8a8a8a"}]
    },
    {
      featureType: "road.arterial",
      elementType: "geometry",
      stylers: [{color: "#373737"}]
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [{color: "#3c3c3c"}]
    },
    {
      featureType: "road.highway.controlled_access",
      elementType: "geometry",
      stylers: [{color: "#4e4e4e"}]
    },
    {
      featureType: "road.local",
      elementType: "labels.text.fill",
      stylers: [{color: "#616161"}]
    },
    {
      featureType: "transit",
      elementType: "labels.text.fill",
      stylers: [{color: "#757575"}]
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{color: "#000000"}]
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [{color: "#3d3d3d"}]
    }
  ];

  const mapStyles = [
    {
      featureType: "all",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#7c93a3"
        },
        {
          lightness: "-10"
        }
      ]
    },
    {
      featureType: "administrative.country",
      elementType: "geometry",
      stylers: [
        {
          visibility: "on"
        }
      ]
    },
    {
      featureType: "administrative.country",
      elementType: "geometry.stroke",
      stylers: [
        {
          color: "#c2d1d6"
        }
      ]
    },
    {
      featureType: "landscape",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#dde3e3"
        }
      ]
    },
    {
      featureType: "road.highway",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#c2d1d6"
        }
      ]
    },
    {
      featureType: "road.highway",
      elementType: "geometry.stroke",
      stylers: [
        {
          color: "#a9b4b8"
        },
        {
          lightness: "0"
        }
      ]
    },
    {
      featureType: "water",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#a3c7df"
        }
      ]
    }
  ];
  const [color, setColor] = useState("red");
  const [showAllVehicle, setshowAllVehicle] = useState(false);
  const [mapZoom, setMapZoom] = useState<number>(14);
  const {isLoaded, loadError} = useJsApiLoader({
    googleMapsApiKey: mapKey
  });
  const mapRef = useRef<google.maps.Map | null>(null);

  const {selectedVehicle, gpsLocations, filterValues} = useLiveContext();
  const {data} = useAllVehicleContext();
  const {filterContext} = useNeoContext();
  const prevSelectedVehicle = useRef(selectedVehicle);

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const schoolLat = Number(filterContext?.client?.insLat);
  const schoolLng = Number(filterContext?.client?.insLng);
  const DEFAULT_CENTER = useMemo(
    () =>
      Number.isFinite(schoolLat) && Number.isFinite(schoolLng)
        ? {lat: schoolLat, lng: schoolLng}
        : {lat: 11.0168, lng: 76.9558},
    [schoolLat, schoolLng]
  );
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [selectedMarker, setSelectedMarker] = useState<any | null>(null);
  const [markerPositions, setMarkerPositions] = useState<
    (NeoMap & {image?: string})[]
  >([]);
  const [boardingPoints, setBoardingPoints] = useState<NeoMap[]>([]);
  const [refRoutePolylinePaths, setRefRoutePolylinePaths] = useState<
    google.maps.LatLng[]
  >([]);
  const [routes, setRoutes] = useState<google.maps.LatLng[]>([]);
  const [paths, setPaths] = useState<NeoMap[]>([]);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [hoverWindowInfo, setHoverWindowInfo] = useState<HoverInfo>(
    {} as HoverInfo
  );
  const [showHoverInfo, setShowHoverInfo] = useState<boolean>(false);
  const [lastLocations, setLastLocations] = useState<{
    [vehicleId: string]: {
      lastTime: number;
    };
  }>(null as any);
  const [colorMarker, setColorMarker] = useState<ColorStationary[]>([]);
  const [colorMarkerInfo, setColorMarkerInfo] = useState<any>(null);
  const [bplocationInfo, setBplocationInfo] = useState<any>(null);
  const setZoom = useCallback(
    (data: NeoMap[]) => {
      if (data.length === 0 || !map) {
        return;
      }
      const position = data[0];
      const latLng = new window.google.maps.LatLng(position.lat!, position.lng);
      map.setCenter(latLng);
      map.setZoom(mapZoom);
    },
    [map, mapZoom]
  );

  const setBound = useCallback(
    (locations: NeoMap[], livePosition: NeoMap) => {
      if (!locations || !map) {
        return;
      }
      const liveLocation = new google.maps.LatLng(
        livePosition.lat!,
        livePosition.lng!
      );
      const bounds = new window.google.maps.LatLngBounds();
      locations.forEach((position: any) => {
        bounds.extend(
          new window.google.maps.LatLng(position.lat, position.lng)
        );
      });
      map.panTo(liveLocation);
    },
    [map]
  );

  useEffect(() => {
    setMapCenter(DEFAULT_CENTER);
    resetLiveMapData();
    setshowAllVehicle(false);
    let tripType = 0;
    const refRoutes: any = [];
    const routedata: any = [];
    if (tripdata?.routedetails) {
      const routedetails = tripdata?.routedetails;
      tripType = routedetails.tripType;
    }

    if (tripdata?.refRouteDetails && tripdata?.refRouteDetails.length > 0) {
      const refRouteDetails = tripdata?.refRouteDetails;
      refRouteDetails.forEach((p: any) => {
        // prefer keys in order of likelihood
        const rawLat = p.lat ?? p.latitude;
        const rawLng = p.lng ?? p.lon ?? p.longitude;

        const lat = Number(rawLat);
        const lng = Number(rawLng);

        // Validate: must be finite numbers and inside valid lat/lng ranges
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return; // skip invalid
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return; // skip out-of-range

        refRoutes.push({lat, lng});
      });
      setRefRoutePolylinePaths(refRoutes as unknown as google.maps.LatLng[]);
    }

    if (tripdata?.history && tripdata?.history.length > 0) {
      const history = tripdata?.history;
      history.forEach((p: any) => {
        // prefer keys in order of likelihood
        const rawLat = p.latitude;
        const rawLng = p.longitude;

        const lat = Number(rawLat);
        const lng = Number(rawLng);

        // Validate: must be finite numbers and inside valid lat/lng ranges
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return; // skip invalid
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return; // skip out-of-range

        routedata.push({lat, lng});
      });
      setRoutes(routedata as unknown as google.maps.LatLng[]);
    }

    if (tripdata?.userBoardings && tripdata?.userBoardings.length > 0) {
      setBoardingPoints(tripdata?.userBoardings);
    }

    if (refRoutes.length > 0 || routedata.length > 0) {
      let polylinedatas = [];
      polylinedatas = refRoutes?.length
        ? [...refRoutes]
        : [...(routedata || [])];
      let lastindex = 0;
      if (polylinedatas.length > 0) {
        lastindex = polylinedatas.length - 1;
        if (tripType == 0) {
          const markerlocation = [
            {
              image: "/images/school_pin_blue.png",
              lat: polylinedatas?.[0].lat,
              lng: polylinedatas?.[0].lng
            },
            {
              image: "/images/parking_pin_grey.png",
              lat: polylinedatas?.[lastindex].lat,
              lng: polylinedatas?.[lastindex].lng
            }
          ];
          setMarkerPositions(markerlocation);
        } else if (tripType == 1) {
          const markerlocation = [
            {
              image: "/images/parking.png",
              lat: polylinedatas?.[0].lat,
              lng: polylinedatas?.[0].lng
            },
            {
              image: "/images/school_pin_grey.png",
              lat: polylinedatas?.[lastindex].lat,
              lng: polylinedatas?.[lastindex].lng
            }
          ];
          setMarkerPositions(markerlocation);
        }
      }
      setZoom([
        {
          lat: polylinedatas?.[lastindex].lat,
          lng: polylinedatas?.[lastindex].lng
        }
      ]);
    }
  }, [DEFAULT_CENTER, page, tripdata]);

  const resetLiveMapData = () => {
    setRefRoutePolylinePaths([]);
    setColorMarker([]);
    setColorMarkerInfo(null);
    setSelectedMarker([]);
    setShowInfo(false);
    setRoutes([]);
    setMarkerPositions([]);
    setBoardingPoints([]);
    setBplocationInfo(null);
  };

  const containerStyle = {
    width: "100%",
    height: "auto"
  };

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    setMap(map);
  };

  const handleMarkerClick = (position: NeoMap, totalDisVal: any = "") => {
    setSelectedMarker(() => null);
    const updatedMarkerData = {...position}; // Create a copy of markerData to avoid mutating the original object

    if (!showAllVehicle) {
      const date = calcTime(updatedMarkerData.ti as number);
      const speed = updatedMarkerData.sp;
      const totalDistance = totalDisVal
        ? (totalDisVal / 1000).toFixed(2)
        : selectedVehicle?.distance;

      const formattedDistance = totalDistance?.toString().includes("km")
        ? totalDistance
        : `${totalDistance} km`;

      const lat = updatedMarkerData.lat;
      const lng = updatedMarkerData.lng;
      const selectedMarkData = {
        name: selectedVehicle?.name,
        date: date,
        speed: speed,
        totalDistance: formattedDistance,
        lat: lat,
        lng: lng
      };
      setSelectedMarker(selectedMarkData);
      setShowInfo(true);
    } else {
      const lat = updatedMarkerData.lat;
      const lng = updatedMarkerData.lng;
      const name = updatedMarkerData.name;
      const distance = updatedMarkerData?.totalDistance;
      const date = updatedMarkerData.date;
      const speed = updatedMarkerData.speed;
      //const date = updatedMarkerData.tripDate;
      const selectedMarkData = {
        name: name,
        date: date,
        speed: speed,
        totalDistance: distance,
        lat: lat,
        lng: lng
      };
      setSelectedMarker(selectedMarkData);
      setShowInfo(true);
    }
  };

  const handlePlaybackUpdate = (event: TPlaybackData) => {
    //console.log(event);
    if (event && event.coordinates) {
      setMarkerPositions([event.coordinates]);
    }
  };

  const setPlaybackPolylinePaths = (routes: NeoMap[]) => {
    setRoutes(routes as unknown as google.maps.LatLng[]);
    if (routes.length > 0) {
      const coordinates = routes[0];
      setMapCenter({
        lat: coordinates.lat || 0.0,
        lng: coordinates.lng || 0.0
      });
      setMarkerPositions([coordinates]);
      setMapZoom(12);
    }
  };

  function closestLocation(targetLocation: any, locationData: any) {
    function vectorDistance(dx: any, dy: any) {
      return Math.sqrt(dx * dx + dy * dy);
    }

    function locationDistance(location1: any, location2: any) {
      var dx = location1.lat - location2.lat,
        dy = location1.lng - location2.lng;

      return vectorDistance(dx, dy);
    }

    return locationData.reduce(function (prev: any, curr: any) {
      var prevDistance = locationDistance(targetLocation, prev),
        currDistance = locationDistance(targetLocation, curr);
      return prevDistance < currDistance ? prev : curr;
    });
  }

  const handleMouseOver = (event: google.maps.MapMouseEvent, data: any) => {
    var latLng = event.latLng;
    var eLat = latLng ? latLng.lat().toFixed(8) : "",
      eLng = latLng ? latLng.lng().toFixed(8) : "";

    if (eLat && eLng) {
      const targetLocation = {
        lat: eLat,
        lng: eLng
      };
      const closest = closestLocation(targetLocation, data);

      if (closest && closest.ti !== undefined) {
        setShowHoverInfo(false);
        const latLng = closest.lat + " , " + closest.lng;
        setHoverWindowInfo({
          latLan: event.latLng || null,
          info: {
            speed: closest.sp,
            duration: calcTime(closest.ti),
            latLng: latLng
          }
        });
        setShowHoverInfo(true);
      }
    }

    //console.log(start);
  };

  const handleColorMarkerClick = (markerData: ColorStationary) => {
    setColorMarkerInfo(() => null);
    const updatedMarkerData = {...markerData}; // Create a copy of markerData to avoid mutating the original object
    const newStartTime = calcTime(markerData.staionary_start as number);
    const newEndTime = calcTime(markerData.staionary_end as number);
    updatedMarkerData.staionary_start = newStartTime;
    updatedMarkerData.staionary_end = newEndTime;
    setColorMarkerInfo(updatedMarkerData);
  };

  const generateDirectionMarkers = () => {
    const markers = [];
    for (let i = 0; i < routes.length - 1; i++) {
      const angle = calculateAngle(routes[i], routes[i + 1]);
      markers.push(
        <Marker
          key={i}
          position={routes[i + 1]}
          icon={{
            path: "M0,-5L-10,0L0,5",
            strokeColor: "#FF0000",
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: "#FF0000",
            fillOpacity: 1,
            rotation: angle,
            scale: 1
          }}
        />
      );
    }
    return markers;
  };

  // Calculate the angle between two points
  const calculateAngle = (from: any, to: any) => {
    return (Math.atan2(to.lat - from.lat, to.lng - from.lng) * 180) / Math.PI;
  };

  const handleVehicleRouteName = (
    vehicleName: string,
    vehicleIndex: number
  ) => {
    const parts = vehicleName.split("_");
    // Check if any part is <= 3
    if (parts[0].length <= 3) {
      return parts[0];
    } else {
      return vehicleIndex + 1;
    }
  };

  const handleBpMarkerClick = (position: any) => {
    const updatedMarkerData = {...position};
    const bpmarkerData = {
      bpName: updatedMarkerData.bpName,
      lat: updatedMarkerData.lat,
      lng: updatedMarkerData.lng,
      users: updatedMarkerData.users
    };
    setBplocationInfo(bpmarkerData);
  };

  useEffect(() => {
    const suser = tripdata?.selectedUser;
    if (!suser?.userId) return;
    if (!mapRef.current) return;
    if (!boardingPoints?.length) return;

    const bp = boardingPoints.find((bp: any) =>
      (bp.users || []).some(
        (u: any) => String(u.userId) === String(suser.userId)
      )
    );

    if (!bp) return;

    setBplocationInfo({
      bpName: (bp as any).bpName,
      lat: (bp as any).lat,
      lng: (bp as any).lng,
      users: (bp as any).users,
      bpCovered: (bp as any).bpCovered,
      bpId: (bp as any).bpId
    });

    mapRef.current.panTo({lat: (bp as any).lat, lng: (bp as any).lng});
    mapRef.current.setZoom(16);
  }, [tripdata?.selectedUser?.userId, boardingPoints]);

  return (
    <div className="flex h-full">
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onMapLoad}
          options={{
            styles: mapStyles, // Apply custom theme here
            disableDefaultUI: false // Optional: Hide default controls
          }}
        >
          {filterContext?.client?.insLat !== undefined &&
            filterContext?.client?.insLng !== undefined && (
              <Marker
                position={{
                  lat: schoolLat,
                  lng: schoolLng
                }}
                title={filterContext.client.institutionName || "School"}
                icon={{
                  url: "/images/school_3.png",
                  scaledSize: new google.maps.Size(40, 40)
                }}
              />
            )}
          {markerPositions.map((position, index) => (
            <Marker
              key={index}
              icon={{
                url: (position as any).image,
                scale: 4,
                scaledSize: new window.google.maps.Size(40, 40)
              }}
              label={{
                text: showAllVehicle
                  ? (position.name
                      ? handleVehicleRouteName(position.name, index)
                      : "-"
                    ).toString()
                  : "-",
                color: "black"
              }}
              animation={google.maps.Animation.DROP}
              position={{lat: position.lat!, lng: position.lng!}}
              //onClick={() => handleMarkerClick(position)}
            ></Marker>
          ))}

          {boardingPoints.map((position, key) => (
            <Marker
              key={key}
              icon={{
                url: (position as any).bpCovered
                  ? "/images/bpcoverd.svg"
                  : "/images/bpuncoverd.svg",
                scaledSize: new google.maps.Size(30, 30)
              }}
              animation={google.maps.Animation.DROP}
              position={{lat: position.lat!, lng: position.lng!}}
              onClick={() => handleBpMarkerClick(position)}
            ></Marker>
          ))}

          <Polyline
            path={routes}
            options={{strokeColor: "#FACC15", strokeWeight: 3}}
            onMouseOver={event => handleMouseOver(event, routes)}
            onMouseOut={() => setShowHoverInfo(false)}
          />
          {/* {generateDirectionMarkers()} */}
          {showHoverInfo && hoverWindowInfo.latLan && (
            <InfoWindow position={hoverWindowInfo.latLan}>
              <Info type="Speed" data={hoverWindowInfo.info} />
            </InfoWindow>
          )}

          {refRoutePolylinePaths && (
            <Polyline
              path={refRoutePolylinePaths}
              options={{
                strokeColor: "#000",
                strokeOpacity: 0,
                icons: [
                  {
                    icon: {
                      path: "M 0,-2 0,1",
                      strokeOpacity: 1,
                      strokeWeight: 3,
                      scale: 2
                    },
                    offset: "0",
                    repeat: "20px"
                  }
                ]
              }}
            />
          )}
          {bplocationInfo && (
            <InfoWindow
              position={{
                lat: bplocationInfo.lat,
                lng: bplocationInfo.lng
              }}
              onCloseClick={() => setBplocationInfo(null)}
            >
              <Info data={bplocationInfo} type="TripBpMarkerInfo" />
            </InfoWindow>
          )}
        </GoogleMap>
      ) : loadError ? (
        <NeoError message={loadError.message}></NeoError>
      ) : (
        <div className="flex h-[calc(100vh-64px)] w-full items-center justify-center">
          <Spinner />
        </div>
      )}
    </div>
  );
};
